import { useLenis } from 'lenis/react'
import { lazy, useEffect, useRef, useState, type CSSProperties } from 'react'
import { Eyebrow, Lang, Segments } from '@/components/ui'
import { pipeline } from '@/content/site'
import { MEDIA, useMediaQuery } from '@/hooks/useMediaQuery'
import { useReducedMotion } from '@/hooks/useReducedMotion'
import { cx } from '@/lib/cx'
import { EASE, gsap, ScrollTrigger, SplitText, useGSAP } from '@/lib/gsap'
import { SceneCanvas } from '@/webgl/SceneCanvas'
import { usePrefetchAfterIntro } from '@/webgl/prefetch'
import { withRetry } from '@/webgl/retry'
import { useAppStore } from '@/lib/store'
import styles from './Pipeline.module.css'
import { createPipelineState } from './state'

const STEPS = pipeline.steps
const LAST = STEPS.length - 1
const loadPipelineScene = () => import('./PipelineScene')
const PipelineScene = lazy(() =>
  withRetry(loadPipelineScene).then((module) => ({ default: module.PipelineScene })),
)
/** After the intro: fetch the scene and precompute its particle forms in idle time. */
const warmPipelineScene = () =>
  Promise.all([
    loadPipelineScene(),
    import('./forms').then((module) => module.warmPipeline(useAppStore.getState().quality)),
  ])

const easeInOutCubic = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2)

/**
 * "How it works": pinned for four screens. One halftone particle system morphs through the
 * four forms on the right while the steps swap on the left (masked lines) and an iris rail
 * fills. Scroll comes to rest on each step's centre. Reduced motion: no pin and no scrub;
 * the steps stack and the art beside them shows each form resolved and still.
 */
export function Pipeline() {
  usePrefetchAfterIntro(warmPipelineScene)
  const sectionRef = useRef<HTMLElement>(null)
  const stageRef = useRef<HTMLDivElement>(null)
  const artRef = useRef<HTMLDivElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)
  const listRef = useRef<HTMLOListElement>(null)
  const triggerRef = useRef<ScrollTrigger | null>(null)
  const splitsRef = useRef<SplitText[]>([])
  const activeRef = useRef(0)
  const [state] = useState(createPipelineState)
  const [active, setActive] = useState(0)
  const reducedMotion = useReducedMotion()
  const coarsePointer = useMediaQuery(MEDIA.coarsePointer)
  const lenis = useLenis()

  // Measure the art area (relative to the canvas) for the scene's layout.
  useEffect(() => {
    const art = artRef.current
    const canvas = canvasRef.current
    if (!art || !canvas) return
    const measure = () => {
      const box = canvas.getBoundingClientRect()
      const room = art.getBoundingClientRect()
      const layout = state.layout
      layout.artX = room.left - box.left
      layout.artY = room.top - box.top
      layout.artW = room.width
      layout.artH = room.height
      layout.version += 1
      state.invalidate?.()
    }
    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(art)
    observer.observe(canvas)
    return () => observer.disconnect()
  }, [state, reducedMotion])

  // Pin + scrub. A proxy carries the scrubbed progress to the scene and the rail.
  useGSAP(
    () => {
      const stage = stageRef.current
      const list = listRef.current
      if (!stage || !list) return
      const items = gsap.utils.toArray<HTMLElement>('[data-step]', list)

      if (reducedMotion) {
        state.canvasTop = () => canvasRef.current?.getBoundingClientRect().top ?? 0
        items.forEach((item) => item.style.setProperty('--fill', '1'))
        return
      }

      const setFill = items.map((item) => gsap.quickSetter(item, '--fill'))
      const onProgress = (value: number) => {
        items.forEach((_, k) => setFill[k](clamp01(value * LAST - (k - 1))))
        const index = Math.round(value * LAST)
        if (index !== activeRef.current) setActive(index)
      }
      const proxy = state.progress
      proxy.value = 0
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: stage,
          start: 'top top',
          end: '+=400%',
          pin: true,
          scrub: 1.1,
          anticipatePin: 1,
          invalidateOnRefresh: true,
        },
      })
      tl.to(proxy, { value: 1, ease: 'none', duration: 1, onUpdate: () => onProgress(proxy.value) })
      onProgress(0)
      const trigger = tl.scrollTrigger ?? null
      triggerRef.current = trigger
      state.canvasTop = () => {
        if (!trigger) return 0
        const y = window.scrollY
        if (y < trigger.start) return trigger.start - y
        if (y > trigger.end) return trigger.end - y
        return 0
      }

      // Masked line swaps for the step bodies. Split once; autoSplit re-splits on resize.
      splitsRef.current = items.map((item, k) =>
        SplitText.create(item.querySelectorAll('[data-body] p'), {
          type: 'lines',
          mask: 'lines',
          aria: 'none',
          autoSplit: true,
          onSplit: (self) => {
            gsap.set(self.lines, { yPercent: k === activeRef.current ? 0 : 105 })
          },
        }),
      )
      return () => {
        triggerRef.current = null
        splitsRef.current = []
      }
    },
    { scope: sectionRef, dependencies: [reducedMotion, state], revertOnUpdate: true },
  )

  // Swap the visible step body when the active step changes.
  useGSAP(
    () => {
      const previous = activeRef.current
      activeRef.current = active
      if (reducedMotion || previous === active) return
      const outgoing = splitsRef.current[previous]?.lines ?? []
      const incoming = splitsRef.current[active]?.lines ?? []
      gsap.to(outgoing, {
        yPercent: -105,
        duration: 0.7,
        stagger: 0.03,
        ease: EASE.inOut,
        overwrite: true,
      })
      gsap.fromTo(
        incoming,
        { yPercent: 105 },
        { yPercent: 0, duration: 1, delay: 0.16, stagger: 0.05, ease: EASE.out, overwrite: true },
      )
    },
    { dependencies: [active, reducedMotion] },
  )

  // A gentle snap: when scrolling comes to rest inside the pin, glide to the nearest step.
  useEffect(() => {
    // Native touch swipes must keep their progress instead of snapping back after each lift.
    if (reducedMotion || coarsePointer || !lenis) return
    const onScrollEnd = () => {
      const trigger = triggerRef.current
      if (!trigger) return
      const y = window.scrollY
      const span = trigger.end - trigger.start
      if (span <= 0 || y < trigger.start - 1 || y > trigger.end + 1) return
      const target = trigger.start + (Math.round(((y - trigger.start) / span) * LAST) / LAST) * span
      if (Math.abs(target - y) < 3) return
      lenis.scrollTo(target, { duration: 1.1, easing: easeInOutCubic })
    }
    ScrollTrigger.addEventListener('scrollEnd', onScrollEnd)
    return () => ScrollTrigger.removeEventListener('scrollEnd', onScrollEnd)
  }, [lenis, reducedMotion, coarsePointer])

  // Reduced motion: the step in view picks the (still) form.
  useEffect(() => {
    const list = listRef.current
    if (!reducedMotion || !list) return
    const items = Array.from(list.querySelectorAll<HTMLElement>('[data-step]'))
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          const index = items.indexOf(entry.target as HTMLElement)
          if (index < 0 || index === state.active) continue
          state.active = index
          const canvas = canvasRef.current
          if (canvas) {
            gsap.fromTo(canvas, { autoAlpha: 0.2 }, { autoAlpha: 1, duration: 0.3, ease: 'none' })
          }
          state.invalidate?.()
        }
      },
      { rootMargin: '-45% 0px -45% 0px' },
    )
    items.forEach((item) => observer.observe(item))
    return () => observer.disconnect()
  }, [reducedMotion, state])

  return (
    <section
      ref={sectionRef}
      id="pipeline"
      aria-labelledby="pipeline-title"
      className={styles.root}
      data-theme="light"
      data-mode={reducedMotion ? 'stacked' : 'pinned'}
    >
      <div ref={stageRef} className={styles.stage}>
        <div ref={canvasRef} className={styles.canvas}>
          <SceneCanvas
            id="pipeline"
            label={pipeline.sceneLabel}
            gl={{ alpha: false }}
            camera={{ position: [0, 0, 7.5], fov: 30 }}
          >
            <PipelineScene state={state} />
          </SceneCanvas>
        </div>

        <div className={styles.inner}>
          <div ref={artRef} className={styles.art} aria-hidden="true" />

          <header className={styles.head}>
            <Eyebrow>{pipeline.eyebrow}</Eyebrow>
            <h2 id="pipeline-title" className={styles.title}>
              <Segments segments={pipeline.title} />
            </h2>
          </header>

          <ol ref={listRef} className={styles.steps} role="list">
            {STEPS.map((step, index) => (
              <li
                key={step.id}
                className={cx(styles.step, index === active && styles.active)}
                style={{ '--row': index + 1, '--fill': 0 } as CSSProperties}
                data-step
                aria-current={!reducedMotion && index === active ? 'step' : undefined}
              >
                <h3 className={styles.stepHead}>
                  <span className={styles.index}>{step.index}</span>
                  <span className={styles.stepTitle}>{step.title}</span>
                </h3>
                <div className={styles.body} data-body>
                  <p>{step.body}</p>
                  {step.quote ? (
                    <figure className={styles.quote}>
                      <blockquote>
                        <p>
                          <Lang code="hi-Latn">{step.quote}</Lang>
                        </p>
                      </blockquote>
                      <figcaption>
                        <p>{step.quoteEn}</p>
                      </figcaption>
                    </figure>
                  ) : null}
                </div>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  )
}

function clamp01(value: number): number {
  return value < 0 ? 0 : value > 1 ? 1 : value
}
